import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaDiscord, FaSearch } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { GET_COMMUNITY_TIPPERS } from "../../../../queries/mutation/broadcaster/queries";
import { Tipper } from "../../../../types/Tipper";
import LoadingPage from "../../../LoadingPage";
import TipperDetail from "../../TipperDetail";
import { timeAgo, tokensToDollars } from "./format";
import StatTile from "./StatTile";

type CommunityTipper = {
  id: string;
  username: string;
  lifetimeTips: number;
  lifetimeBiggestTip: number;
  inFanclub: boolean;
  lastSeen: string | null;
  reinUserId: string | null;
  reinUsername: string | null;
  crimcoins: number | null;
  hasDiscord: boolean;
};

const PAGE_SIZE = 100;

const SORTS = [
  { value: "tips", label: "Most tipped" },
  { value: "biggest", label: "Biggest single tip" },
  { value: "recent", label: "Recently active" },
  { value: "name", label: "Username A-Z" },
];

const TippersTab = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [inFanclub, setInFanclub] = useState(false);
  const [linked, setLinked] = useState(false);
  const [sort, setSort] = useState("tips");
  // The open profile is in the address (?tipper=name), so a profile can be
  // linked to from elsewhere (the dashboard's banked tips) and shared.
  const [params, setParams] = useSearchParams();
  const selectedName = params.get("tipper");
  const selected = selectedName ? { username: selectedName } : null;
  const setSelected = (t: { username: string } | null) =>
    setParams(t ? { tab: "tippers", tipper: t.username } : { tab: "tippers" });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // "Show more" belongs to the search and filters it was clicked under, so a
  // new search starts from the top in one fetch rather than first refetching
  // the old row count and then resetting it.
  const filterKey = JSON.stringify([debouncedSearch, inFanclub, linked, sort]);
  const [shown, setShown] = useState({ key: filterKey, limit: PAGE_SIZE });
  const limit = shown.key === filterKey ? shown.limit : PAGE_SIZE;

  const { data, previousData, loading, error, refetch } = useQuery(
    GET_COMMUNITY_TIPPERS,
    {
      variables: {
        search: debouncedSearch || undefined,
        inFanclub: inFanclub || undefined,
        linked: linked || undefined,
        sort,
        limit,
      },
      fetchPolicy: "cache-and-network",
    },
  );

  // Keep the last results on screen while the next ones load, rather than
  // flashing a spinner on every keystroke.
  const page = (data ?? previousData)?.getCommunityTippers;
  const tippers: CommunityTipper[] = page?.tippers ?? [];

  const list = (
    <div
      className={
        selected
          ? "hidden"
          : "card bg-base-100 flex-1 min-h-0 flex flex-col shadow"
      }
    >
      <div className="card-body flex flex-col min-h-0 !p-2 sm:!p-4 gap-3">
        {page && (
          <div className="grid grid-cols-3 gap-2">
            <StatTile label="Tippers" value={page.totalTippers} />
            <StatTile
              label="In fan club"
              value={page.inFanclubCount}
              className="text-success"
            />
            <StatTile
              label="On Reinstorm"
              value={page.linkedCount}
              className="text-primary"
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <label className="input input-bordered input-sm flex items-center gap-2 flex-1">
            <FaSearch className="opacity-50" />
            <input
              type="search"
              className="grow"
              placeholder="Search CB username"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              className={`btn btn-sm ${inFanclub ? "btn-success" : "btn-outline"}`}
              onClick={() => setInFanclub(!inFanclub)}
              aria-pressed={inFanclub}
            >
              In fan club
            </button>
            <button
              className={`btn btn-sm ${linked ? "btn-primary" : "btn-outline"}`}
              onClick={() => setLinked(!linked)}
              aria-pressed={linked}
            >
              On Reinstorm
            </button>
            <select
              className="select select-bordered select-sm"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort tippers"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!page && loading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingPage />
          </div>
        ) : error && !page ? (
          <div className="text-center py-8">
            <div className="text-error font-bold mb-2">
              Couldn't load tippers
            </div>
            <div className="text-sm text-base-content/60 mb-3">
              {error.message}
            </div>
            <button className="btn btn-sm" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : tippers.length === 0 ? (
          <div className="text-center py-8 text-base-content/60">
            No tippers match
            {debouncedSearch ? ` "${debouncedSearch}"` : " these filters"}
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="table table-sm table-pin-rows">
              <thead>
                <tr>
                  <th>Tipper</th>
                  <th className="text-right">Lifetime tips</th>
                  <th className="text-right hidden sm:table-cell">
                    Biggest tip
                  </th>
                  <th className="hidden md:table-cell">Last seen</th>
                  <th className="hidden sm:table-cell">Reinstorm</th>
                </tr>
              </thead>
              <tbody className={loading ? "opacity-60" : ""}>
                {tippers.map((t) => (
                  <tr
                    key={t.id}
                    className="hover cursor-pointer"
                    tabIndex={0}
                    onClick={() => setSelected(t)}
                    onKeyDown={(e) => e.key === "Enter" && setSelected(t)}
                  >
                    <td>
                      <div className="font-semibold break-all">
                        {t.username}
                      </div>
                      {t.inFanclub && (
                        <span className="badge badge-success badge-xs">
                          fan club
                        </span>
                      )}
                      {/* The Reinstorm column is hidden on phones. */}
                      {t.reinUsername && (
                        <div className="sm:hidden text-xs text-primary flex items-center gap-1">
                          {t.hasDiscord && <FaDiscord />}
                          {t.reinUsername} ·{" "}
                          {(t.crimcoins ?? 0).toLocaleString()} CC
                        </div>
                      )}
                    </td>
                    <td className="text-right">
                      <div>{t.lifetimeTips.toLocaleString()}</div>
                      <div className="text-xs text-success">
                        {tokensToDollars(t.lifetimeTips)}
                      </div>
                    </td>
                    <td className="text-right hidden sm:table-cell">
                      {t.lifetimeBiggestTip.toLocaleString()}
                    </td>
                    <td className="hidden md:table-cell text-sm">
                      {timeAgo(t.lastSeen)}
                    </td>
                    <td className="hidden sm:table-cell">
                      {t.reinUsername ? (
                        <div className="flex items-center gap-1">
                          <span className="truncate max-w-[8rem]">
                            {t.reinUsername}
                          </span>
                          {t.hasDiscord && (
                            <FaDiscord
                              className="text-primary shrink-0"
                              title="Discord linked"
                            />
                          )}
                          <span className="text-xs text-secondary whitespace-nowrap">
                            {(t.crimcoins ?? 0).toLocaleString()} CC
                          </span>
                        </div>
                      ) : (
                        <span className="text-base-content/40">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {page && tippers.length > 0 && (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-base-content/60">
              Showing {tippers.length.toLocaleString()} of{" "}
              {page.total.toLocaleString()}
            </span>
            {tippers.length < page.total && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() =>
                  setShown({ key: filterKey, limit: limit + PAGE_SIZE })
                }
                disabled={loading}
              >
                {loading ? "Loading..." : "Show more"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {list}
      {selected && (
        <TipperDetail
          tipper={{ username: selected.username } as Tipper}
          clearTipper={() => setSelected(null)}
          refreshTipper={async () => {
            await refetch();
          }}
        />
      )}
    </>
  );
};

export default TippersTab;
